import React, { useState } from "react";
import { createPortal } from "react-dom";
import classnames from "classnames";
import useMediaQuery from "@mui/material/useMediaQuery";
import LoginForm from "../forms/auth/LoginForm";
import SignupForm from "../forms/auth/SignupForm";
import CheckInbox from "../forms/password-reset/CheckInbox";
import ForgotPasswordForm from "../forms/password-reset/ForgotPasswordForm";
import { CloseIcon, BinifyLogo } from "@components/library/Icons";
import ArrowBackIcon from "@assets/icons/ArrowBackIcon";
import useBodyScrollLock from "@helpers/useBodyScrollLock";
import "./AuthModal.sass";

const AuthModal = ({ isOpen, onClose, view, setView }) => {
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const [currentView, setCurrentView] = useState(view || "login");
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  React.useEffect(() => {
    if (view) {
      setCurrentView(view);
    }
  }, [view]);

  React.useEffect(() => {
    if (isOpen) {
      setShouldRender(true);

      const animationFrame = requestAnimationFrame(() => {
        setIsAnimating(true);
      });

      return () => cancelAnimationFrame(animationFrame);
    }

    setIsAnimating(false);

    const timer = setTimeout(() => {
      setShouldRender(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [isOpen]);

  useBodyScrollLock(isAnimating);

  const handleSetView = (newView) => {
    setCurrentView(newView);

    if (setView) {
      setView(newView);
    }
  };

  if (!shouldRender || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      data-modal="auth-modal-overlay"
      className={classnames(
        "auth-modal-overlay",
        isAnimating
          ? "auth-modal-overlay--open"
          : "auth-modal-overlay--closed",
      )}
      onClick={onClose}
    >
      <div
        data-modal="auth-modal"
        className={classnames(
          "auth-modal",
          isAnimating ? "auth-modal--open" : "",
          isMobile ? "auth-modal--mobile" : "",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {isMobile ? (
          <>
            <div className="auth-modal__header-mobile">
              <div className="auth-modal__header-gradient" />

              <div className="auth-modal__header-content">
                <button
                  type="button"
                  onClick={onClose}
                  className="auth-modal__back-button"
                >
                  <ArrowBackIcon />
                </button>

                <BinifyLogo height="18" />

                <div className="auth-modal__header-spacer" />
              </div>
            </div>

            <div className="auth-modal__content auth-modal__content--mobile">
              {currentView === "check-inbox" && (
                <div className="auth-page__overlay" />
              )}

              {currentView === "login" && (
                <LoginForm setView={handleSetView} isMobile />
              )}

              {currentView === "signup" && (
                <SignupForm setView={handleSetView} isMobile />
              )}

              {currentView === "forgot-password" && (
                <ForgotPasswordForm
                  setView={handleSetView}
                  isMobile
                />
              )}

              {currentView === "check-inbox" && (
                <CheckInbox setView={handleSetView} isMobile />
              )}
            </div>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onClose}
              className="auth-modal__close-button"
            >
              <CloseIcon />
            </button>

            <div className="auth-modal__content">
              {currentView === "login" && (
                <LoginForm setView={handleSetView} />
              )}

              {currentView === "signup" && (
                <SignupForm setView={handleSetView} />
              )}

              {currentView === "forgot-password" && (
                <ForgotPasswordForm setView={handleSetView} />
              )}

              {currentView === "check-inbox" && (
                <CheckInbox setView={handleSetView} />
              )}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default AuthModal;
